import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { attachSentryUser } from '@/sentry';
import type { AuthUser } from '@/stores/auth';

const DEV_PERSONAS: AuthUser[] = [
  { id: 'dev-admin', name: 'Igor (Admin)', email: 'admin@thermoduct.be', roles: ['admin'], odooId: 29 },
  { id: 'dev-pl', name: 'David (PL)', email: 'david.marijnissen@thermoduct.be', roles: ['projectleider'], odooId: 21 },
  { id: 'dev-pl-jorn', name: 'Jorn (PL)', email: 'jorn.lauman@thermoduct.be', roles: ['projectleider'], odooId: 14 },
  { id: 'dev-pl-ek', name: 'PL EasyKit', email: 'pl-ek@thermoduct.be', roles: ['projectleider_easykit'], odooId: null },
  { id: 'dev-aa', name: 'A&A (Installer)', email: 'aa@thermoduct.be', roles: ['installer'], odooId: 37802 },
  { id: 'dev-oa-anatolie', name: 'Anatolie (Onderaannemer)', email: 'anatoliestratular7@gmail.com', roles: ['installer'], odooId: 35287 },
  { id: 'dev-oa-vakfloor', name: 'Vakfloor (Onderaannemer)', email: 'hugo@vakfloor.com', roles: ['installer'], odooId: 39267 },
  { id: 'dev-interventie', name: 'Interventie', email: 'interventie@thermoduct.be', roles: ['interventie'], odooId: 37735 },
  { id: 'dev-easykit', name: 'Dries (EasyKit Driver)', email: 'easykit@thermoduct.be', roles: ['easykit'], odooId: 37732 },
  { id: 'dev-warehouse', name: 'Magazijn', email: 'magazijn@thermoduct.be', roles: ['warehouse'], odooId: null },
  { id: 'dev-administratie', name: 'Administratie', email: 'administratie@thermoduct.be', roles: ['administratie'], odooId: null },
  { id: 'dev-administratie-oplijster', name: 'Administratie + Oplijster', email: 'administratie-oplijster@thermoduct.be', roles: ['administratie', 'oplijster', 'projectleider_easykit'], odooId: null },
  { id: 'dev-oplijster', name: 'Oplijster', email: 'oplijster@thermoduct.be', roles: ['oplijster'], odooId: null },
  { id: 'dev-oplijster-ek', name: 'Adrian (EK Oplijster)', email: 'adrian.wiecierz@thermoduct.be', roles: ['oplijster_easykit'], odooId: 22 },
  { id: 'a-bright-test-1', name: '[A] Bright test', email: 'a-bright-test@thermoduct.test', roles: ['bright-api'], odooId: null, authTime: Math.floor(Date.now() / 1000) },
];

const VIEW_ROUTE_ORDER = ['tasks', 'planning', 'easykit_planning', 'easykit_oplijster', 'project', 'installer', 'interventie', 'easykit_driver', 'admin', 'bright_api'] as const;

const isDevMode = import.meta.env.VITE_SKIP_AUTH === 'true';

export function DevPersonaSwitcher() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);

  // In production: check if real user is admin (from JWT)
  // The override may change user.roles, but we need the original JWT roles
  const isRealAdmin = !isDevMode && user?.roles.includes('admin') && !localStorage.getItem('dev-role-override');
  const hasOverride = !isDevMode && !!localStorage.getItem('dev-role-override');
  const canShow = isDevMode || isRealAdmin || hasOverride;

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  if (!canShow || !user) return null;

  const currentRole = user.roles[0] ?? 'unknown';
  const overrideLabel = hasOverride ? ` (override)` : '';

  async function switchPersona(persona: AuthUser) {
    const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

    if (isDevMode) {
      // Dev mode: swap the entire user in the store
      useAuthStore.getState().setAuth('dev-token', persona);
      localStorage.setItem('dev-persona', JSON.stringify(persona));
    } else {
      // Production: store role override, re-fetch permissions with overridden roles
      if (persona.roles.includes('admin')) {
        // Switching back to admin = clear override
        localStorage.removeItem('dev-role-override');
      } else {
        localStorage.setItem('dev-role-override', JSON.stringify({
          roles: persona.roles,
          odooId: persona.odooId,
        }));
      }
      // Re-fetch permissions (API will see X-Dev-Persona header and return overridden permissions)
      usePermissionsStore.getState().clearPermissions();
      usePermissionsStore.getState().fetchPermissions();
      // Re-attach Sentry tags so impersonation state is reflected immediately
      attachSentryUser(useAuthStore.getState().user);
    }

    // Wait for permissions to load, then route based on permissions
    await new Promise((resolve) => setTimeout(resolve, 300));

    const { canView } = usePermissionsStore.getState();
    for (const view of VIEW_ROUTE_ORDER) {
      if (canView(view)) {
        if (view === 'installer') {
          window.location.href = BASE + '/installer';
          return;
        }
        if (view === 'interventie') {
          window.location.href = BASE + '/interventie';
          return;
        }
        if (view === 'project') {
          window.location.href = BASE + '/tasks';
          return;
        }
        if (view === 'easykit_planning') {
          window.location.href = BASE + '/easykit';
          return;
        }
        if (view === 'easykit_oplijster') {
          window.location.href = BASE + '/easykit-oplijster';
          return;
        }
        if (view === 'easykit_driver') {
          window.location.href = BASE + '/easykit/driver';
          return;
        }
        if (view === 'bright_api') {
          window.location.href = BASE + '/bright';
          return;
        }
        window.location.href = `${BASE}/${view}`;
        return;
      }
    }

    window.location.href = BASE + '/tasks';
  }

  return (
    <div ref={containerRef} className="fixed bottom-20 right-4 z-[60] lg:bottom-4">
      {open && (
        <div className="mb-2 w-64 rounded-lg bg-white shadow-lg ring-1 ring-black/10 max-h-[60vh] overflow-y-auto">
          <div className="px-3 py-2 text-xs font-semibold text-violet-600 uppercase tracking-wide border-b border-gray-100">
            {isDevMode ? 'Dev Persona' : 'Role Override'}
          </div>
          <ul className="py-1">
            {/* The Bright persona is for the SKIP_AUTH stack only: as a live role override it
                would make real Bright keys under the admin's own account. */}
            {DEV_PERSONAS.filter((p) => isDevMode || p.authTime === undefined).map((persona) => {
              const isActive = isDevMode
                ? user.email === persona.email && user.roles[0] === persona.roles[0]
                : hasOverride
                  ? user.roles[0] === persona.roles[0]  // won't match since override doesn't change store
                  : persona.roles.includes('admin');  // admin is "active" when no override
              return (
                <li key={persona.id}>
                  <button
                    type="button"
                    onClick={() => switchPersona(persona)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-violet-50 transition-colors"
                  >
                    <span className="w-5 shrink-0 text-violet-600">
                      {isActive ? (
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </span>
                    <span className="flex-1 text-left">
                      <span className="block font-medium">{persona.name}</span>
                      <span className="block text-xs text-gray-400">{persona.roles[0]}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded-full bg-violet-600 px-3 py-1.5 text-xs font-medium text-white shadow-md hover:bg-violet-700 transition-colors"
      >
        <span className="w-2 h-2 rounded-full bg-violet-300 shrink-0" />
        {isDevMode ? `${user.name} (${currentRole})` : `Override: ${hasOverride ? currentRole : 'none'}${overrideLabel}`}
      </button>
    </div>
  );
}
